import { useState } from 'react';
import { Button, messageFromError, TextField, toast } from '@eo/shared';

import { fetchGoogleStartUrl, fetchSlackStartUrl } from './api/api';
import { ProviderSteps } from './ProviderSteps';
import type { ProviderConnectFormProps } from './types';

export function ProviderConnectForm({
  descriptor,
  status,
  onSaveToken,
  onDone,
}: ProviderConnectFormProps) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startOAuth = async () => {
    setBusy(true);
    try {
      const { url } =
        descriptor.key === 'slack' ? await fetchSlackStartUrl() : await fetchGoogleStartUrl();
      window.location.assign(url);
    } catch (err) {
      toast.error(
        messageFromError(err, `Starting the ${descriptor.title} sign-in failed. Try again.`),
      );
      setBusy(false);
    }
  };

  const save = async () => {
    if (descriptor.kind !== 'token' || !draft.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSaveToken(draft.trim());
      setDraft('');
      toast.success(`${descriptor.title} ${descriptor.noun} saved.`);
      onDone();
    } catch (err) {
      // Inline as well as toasted: a rejected token is about the field, and the
      // toast is gone by the time the next paste happens.
      const message = messageFromError(err, `Saving the ${descriptor.noun} failed. Try again.`);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-text-tertiary">{descriptor.description}</p>

      <ProviderSteps steps={descriptor.steps} link={descriptor.link} />

      {descriptor.kind === 'oauth' ? (
        <Button className="w-fit" onClick={() => void startOAuth()} disabled={busy}>
          {status.connected ? `Reconnect ${descriptor.title}` : `Connect ${descriptor.title}`}
        </Button>
      ) : (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <TextField
            id={`${descriptor.key}-${descriptor.noun}`}
            label={`${descriptor.title} ${descriptor.noun}`}
            type="password"
            placeholder={descriptor.placeholder}
            value={draft}
            autoComplete="off"
            error={!!error}
            helperText={error ?? undefined}
            containerClassName="flex-1"
            onChange={(e) => {
              setDraft(e.target.value);
              if (error) setError(null);
            }}
          />
          <Button onClick={() => void save()} disabled={busy || !draft.trim()}>
            {status.connected ? `Replace ${descriptor.noun}` : `Save ${descriptor.noun}`}
          </Button>
        </div>
      )}
    </div>
  );
}
